// Canvas touch gestures — orchestrator för alla mobile-only gester.
//
// Klassifikation per touch-sekvens:
//
//   Block/group-card touch              → block-drag (mouseDown-pathen i
//                                          WorkflowBlock satte upp state:n)
//   Expanded group-frame touch          → group-drag (start vid > THRESHOLD
//                                          rörelse, annars tap = select)
//   Tom canvas, 1 finger:
//     - rörelse                         → LIVE PAN (scroll canvasen)
//     - ingen rörelse + 500ms still     → canvas-long-press
//     - ingen rörelse + touchend        → tap (single/double/triple via
//                                          TapClassifier)
//   Tom canvas, 2 fingrar               → pan + pinch-zoom (kombinerat)
//   2 fingrar tap (kort, ingen rörelse) → undo
//
// Arc-swipe + edge-swipe togs bort i favor av 1-finger pan eftersom de
// konflikterade med pan-UX (start på empty canvas → vi vet inte om user
// vill panna eller svepa).
//
// useEffect-deps är minimala (canvasRef, callbacks via ref) så DOM-
// listeners INTE re-bindas när contentScale ändras under pinch — den
// rebinding-stormen orsakade tidigare jerky pinch + bruten pan.
//
// Reduced-motion: animationer (sheet-byte) sätts via CSS-klass, inte JS.

import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';
import { logger } from '@/lib/logger';
import { createTapClassifier } from '@/lib/gestures/tapClassifier';
import { createLongPressTimer } from '@/lib/gestures/longPressTimer';

export interface TouchDragRefs {
  isDraggingRef: MutableRefObject<boolean>;
  dragBlockIdRef: MutableRefObject<string | null>;
  dragOffsetRef: MutableRefObject<{ x: number; y: number }>;
  canvasOriginXRef: MutableRefObject<number>;
  canvasOriginYRef: MutableRefObject<number>;
  canvasRef: RefObject<HTMLDivElement>;
}

/**
 * Path-tracking för 1-finger-drag — kvar för bakåtkompatibilitet men
 * skrivs inte längre till av denna hook eftersom arc-swipe togs bort.
 * Stannar i interfacet så `GestureTrail`-overlayens props inte ändras.
 */
export interface CanvasGesturePathRef {
  pathRef: MutableRefObject<{ x: number; y: number }[]>;
  tickRef: MutableRefObject<number>;
}

export interface TouchGesturesDeps extends TouchDragRefs {
  contentScale: number;
  setContentScale: (updater: (prev: number) => number) => void;
  minScale: number;
  maxScale: number;
  onUpdateBlockPosition: (id: string, x: number, y: number, moveGroup?: boolean) => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  setIsDragging: (v: boolean) => void;
  setDragBlockId: (v: string | null) => void;

  // ─── Gesture-callbacks (alla optional) ───────────────────────────
  /** 3× tap på tom canvas: öppna sheet-list-väljare. */
  onOpenSheetList?: () => void;
  /** 2× tap på tom canvas: fit-to-screen. */
  onFitToScreen?: () => void;
  /** 2-finger tap (kort, ingen rörelse): undo. */
  onUndo?: () => void;
  /** Long-press på block/grupp-kort. */
  onBlockLongPress?: (blockId: string, screenX: number, screenY: number) => void;
  onGroupCardLongPress?: (groupId: string, screenX: number, screenY: number) => void;
  onCanvasLongPress?: (screenX: number, screenY: number) => void;
  /** Touch-init av drag på expanded group-frame. */
  onGroupDragStart?: (groupId: string, screenX: number, screenY: number) => void;
  /** Touch-move-uppdatering för pågående group-drag (i canvas-inner-coord). */
  onGroupDragMove?: (groupId: string, currentX: number, currentY: number) => void;
  /** Committa drag av block (efter rörelse > THRESHOLD). canvas-touch håller
   *  drag-state pending tills nu så long-press inte kan cancellas av jitter. */
  onBlockDragCommit?: (blockId: string, screenX: number, screenY: number) => void;
  /** Committa drag av collapsed group-card (samma rationale). */
  onCollapsedGroupDragCommit?: (groupId: string, screenX: number, screenY: number) => void;
  /** Horizontal swipe vid sheet-strip → byt aktiv sheet. */
  onSheetSwitch?: (direction: 'fwd' | 'back') => void;
  /** Edge-swipe från vänster → öppna left sidebar. */
  onOpenLeftSidebar?: () => void;
  /** Edge-swipe från höger → öppna right sidebar. */
  onOpenRightSidebar?: () => void;
  /** Edge-swipe från top → öppna top-bar. */
  onOpenTopBar?: () => void;

  /** Live path-tracker — tom array efter arc-swipe-borttagning, men ref:en
   *  bevaras så GestureTrail-overlayens props inte ändras. */
  pathRefs?: CanvasGesturePathRef;
}

/** Distance threshold som klassar touch som rörelse vs. tap. */
const TAP_MOVEMENT_THRESHOLD = 10;

export function useCanvasTouchGestures(deps: TouchGesturesDeps) {
  const {
    isDraggingRef, dragBlockIdRef, dragOffsetRef,
    canvasOriginXRef, canvasOriginYRef,
    canvasRef, contentScale, setContentScale,
    minScale, maxScale,
    onUpdateBlockPosition, onUpdateGroupPosition,
    setIsDragging, setDragBlockId,
    onOpenSheetList, onFitToScreen, onUndo,
    onBlockLongPress, onGroupCardLongPress, onCanvasLongPress,
    onGroupDragStart, onGroupDragMove,
    onBlockDragCommit, onCollapsedGroupDragCommit,
    pathRefs,
  } = deps;

  // ── Refs för värden listener:n behöver men som ändras ofta ─────
  // useEffect ska INTE re-binda DOM-listeners när dessa ändras (annars
  // re-binds varje pinch-frame när setContentScale fyrar → state →
  // re-render → re-effect → listeners droppar pågående events).
  const contentScaleRef = useRef(contentScale);
  const minScaleRef = useRef(minScale);
  const maxScaleRef = useRef(maxScale);
  useEffect(() => { contentScaleRef.current = contentScale; }, [contentScale]);
  useEffect(() => { minScaleRef.current = minScale; }, [minScale]);
  useEffect(() => { maxScaleRef.current = maxScale; }, [maxScale]);

  // Callbacks via ref så vi inte behöver re-binda när parent re-render:ar.
  const cbRef = useRef({
    onOpenSheetList, onFitToScreen, onUndo,
    onBlockLongPress, onGroupCardLongPress, onCanvasLongPress,
    onGroupDragStart, onGroupDragMove,
    onBlockDragCommit, onCollapsedGroupDragCommit,
    onUpdateBlockPosition, onUpdateGroupPosition,
    setContentScale, setIsDragging, setDragBlockId,
  });
  useEffect(() => {
    cbRef.current = {
      onOpenSheetList, onFitToScreen, onUndo,
      onBlockLongPress, onGroupCardLongPress, onCanvasLongPress,
      onGroupDragStart, onGroupDragMove,
      onBlockDragCommit, onCollapsedGroupDragCommit,
      onUpdateBlockPosition, onUpdateGroupPosition,
      setContentScale, setIsDragging, setDragBlockId,
    };
  }, [
    onOpenSheetList, onFitToScreen, onUndo,
    onBlockLongPress, onGroupCardLongPress, onCanvasLongPress,
    onGroupDragStart, onGroupDragMove,
    onBlockDragCommit, onCollapsedGroupDragCommit,
    onUpdateBlockPosition, onUpdateGroupPosition,
    setContentScale, setIsDragging, setDragBlockId,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Diagnostik: om denna log spammar konsolen under pan/pinch är
    // useEffect-deps trasig igen — listener:n re-binds varje frame och
    // local state (currentPan etc.) resettas mellan touchmove-events.
    logger.debug('canvas-touch: bind listeners');

    // ── State per touch-sekvens ─────────────────────────────────
    // 2-finger
    let currentPan: { lastX: number; lastY: number; fingers: number } | null = null;
    let lastTouchDist = 0;
    let pendingRaf: number | null = null;
    let pendingDragRaf: number | null = null;
    let pendingDragUpdate:
      | { kind: 'collapsed'; groupId: string; x: number; y: number }
      | { kind: 'group'; groupId: string; currentX: number; currentY: number }
      | { kind: 'block'; blockId: string; x: number; y: number }
      | null = null;
    let groupDragPending: { groupId: string; startX: number; startY: number } | null = null;
    // Block/collapsed-group drag committeras inte förrän rörelse > THRESHOLD
    // — håller long-press-timern levande för tap-and-hold-context-menu.
    let blockDragPending:
      | { kind: 'block' | 'collapsed'; id: string; startX: number; startY: number }
      | null = null;

    // 2-finger tap-detection
    let twoFingerStart: { t: number; midX: number; midY: number; dist: number } | null = null;
    let twoFingerMoved = false;

    // 1-finger på tom canvas: pan + tap-classifier
    let oneFingerStart: { x: number; y: number } | null = null;
    let oneFingerLast: { x: number; y: number } | null = null;
    let oneFingerMoved = false;

    const longPress = createLongPressTimer({
      onFire: (origin) => {
        const el = document.elementFromPoint(origin.x, origin.y) as HTMLElement | null;
        const blockEl = el?.closest('[data-block-id]') as HTMLElement | null;
        const groupCardEl = el?.closest('[data-group-card-id]') as HTMLElement | null;
        if (blockEl) {
          logger.debug(`gesture: long-press → block ${blockEl.getAttribute('data-block-id')}`);
          cbRef.current.onBlockLongPress?.(
            blockEl.getAttribute('data-block-id') ?? '',
            origin.x, origin.y,
          );
        } else if (groupCardEl) {
          logger.debug(`gesture: long-press → group-card ${groupCardEl.getAttribute('data-group-card-id')}`);
          cbRef.current.onGroupCardLongPress?.(
            groupCardEl.getAttribute('data-group-card-id') ?? '',
            origin.x, origin.y,
          );
        } else {
          logger.debug(`gesture: long-press → canvas @(${origin.x},${origin.y})`);
          cbRef.current.onCanvasLongPress?.(origin.x, origin.y);
        }
      },
    });

    const tapClassifier = createTapClassifier({
      onSingle: () => {
        // Single-tap = no-op (handleCanvasClick rensar selection separat)
      },
      onDouble: () => {
        logger.debug('gesture: double-tap → fit-to-screen');
        cbRef.current.onFitToScreen?.();
      },
      onTriple: () => {
        logger.debug('gesture: triple-tap → open-sheet-list');
        cbRef.current.onOpenSheetList?.();
      },
    });

    const clamp = (v: number) =>
      Math.max(minScaleRef.current, Math.min(maxScaleRef.current, v));

    let gestureStart: { kind: 'pinch' | 'pan1' | 'pan2'; t: number; scale: number } | null = null;

    // ── Drag-update via RAF ─────────────────────────────────────
    const applyPendingDragUpdate = () => {
      const update = pendingDragUpdate;
      pendingDragUpdate = null;
      if (!update) return;
      if (update.kind === 'collapsed') {
        cbRef.current.onUpdateGroupPosition(update.groupId, update.x, update.y);
      } else if (update.kind === 'group') {
        cbRef.current.onGroupDragMove?.(update.groupId, update.currentX, update.currentY);
      } else {
        cbRef.current.onUpdateBlockPosition(update.blockId, update.x, update.y, true);
      }
    };
    const scheduleDragUpdate = (update: NonNullable<typeof pendingDragUpdate>) => {
      pendingDragUpdate = update;
      if (pendingDragRaf != null) return;
      pendingDragRaf = requestAnimationFrame(() => {
        pendingDragRaf = null;
        applyPendingDragUpdate();
      });
    };
    const flushPendingDragUpdate = () => {
      if (pendingDragRaf != null) {
        cancelAnimationFrame(pendingDragRaf);
        pendingDragRaf = null;
      }
      applyPendingDragUpdate();
    };
    const cancelPendingDragUpdate = () => {
      if (pendingDragRaf != null) cancelAnimationFrame(pendingDragRaf);
      pendingDragRaf = null;
      pendingDragUpdate = null;
    };

    // pathRefs är kvar för API-stabilitet men inte längre tracked
    const resetPath = () => {
      if (pathRefs) {
        pathRefs.pathRef.current = [];
        pathRefs.tickRef.current += 1;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        // 2-finger gest tar över ev. pågående 1-finger-state.
        longPress.cancel();
        groupDragPending = null;
        blockDragPending = null;
        cancelPendingDragUpdate();
        oneFingerStart = null;
        oneFingerLast = null;
        oneFingerMoved = false;
        resetPath();
        if (isDraggingRef.current) {
          isDraggingRef.current = false;
          dragBlockIdRef.current = null;
          cbRef.current.setIsDragging(false);
          cbRef.current.setDragBlockId(null);
        }
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        currentPan = { lastX: midX, lastY: midY, fingers: 2 };
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastTouchDist = Math.sqrt(dx * dx + dy * dy);
        gestureStart = { kind: 'pinch', t: performance.now(), scale: contentScaleRef.current };
        twoFingerStart = { t: performance.now(), midX, midY, dist: lastTouchDist };
        twoFingerMoved = false;
        return;
      }

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const target = e.target as HTMLElement;
        const groupFrameEl = target.closest('[data-group-frame-id]') as HTMLElement | null;
        const start = { x: touch.clientX, y: touch.clientY };

        // Block- och collapsed-group-card-touch: håll pending tills rörelse
        // > THRESHOLD. Då först committas drag. Mellantid: long-press kan
        // fyra → context-menu via dispatchSyntheticContextMenu.
        const blockEl = target.closest('[data-block-id]') as HTMLElement | null;
        const cardEl = target.closest('[data-group-card-id]') as HTMLElement | null;
        if (blockEl || cardEl) {
          const kind: 'block' | 'collapsed' = cardEl ? 'collapsed' : 'block';
          const id =
            (cardEl?.getAttribute('data-group-card-id') ??
              blockEl?.getAttribute('data-block-id')) ?? '';
          if (id) {
            blockDragPending = { kind, id, startX: touch.clientX, startY: touch.clientY };
            longPress.start(start);
            return;
          }
        }

        // Expanded group-frame: pending state. Drag committeras vid
        // distFromStart > THRESHOLD i touchmove.
        if (groupFrameEl && cbRef.current.onGroupDragStart) {
          const groupId = groupFrameEl.getAttribute('data-group-frame-id') ?? '';
          if (groupId) {
            e.preventDefault();
            groupDragPending = { groupId, startX: touch.clientX, startY: touch.clientY };
            longPress.start(start);
            currentPan = { lastX: touch.clientX, lastY: touch.clientY, fingers: 1 };
            gestureStart = { kind: 'pan1', t: performance.now(), scale: contentScaleRef.current };
            return;
          }
        }

        // Tom canvas — 1 finger. Förbered för pan + tap-classifier.
        oneFingerStart = start;
        oneFingerLast = start;
        oneFingerMoved = false;
        longPress.start(start);
        currentPan = { lastX: touch.clientX, lastY: touch.clientY, fingers: 1 };
        e.preventDefault();
        gestureStart = { kind: 'pan1', t: performance.now(), scale: contentScaleRef.current };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Group-drag pending: vänta tills user rört sig mer än threshold
      // innan vi committar gesture:n som drag.
      if (groupDragPending && e.touches.length === 1) {
        const touch = e.touches[0];
        const distFromStart = Math.hypot(
          touch.clientX - groupDragPending.startX,
          touch.clientY - groupDragPending.startY,
        );
        longPress.observeMove({ x: touch.clientX, y: touch.clientY });
        e.preventDefault();
        if (distFromStart <= TAP_MOVEMENT_THRESHOLD) return;
        const pending = groupDragPending;
        groupDragPending = null;
        longPress.cancel();
        cbRef.current.onGroupDragStart?.(pending.groupId, pending.startX, pending.startY);
        return;
      }

      // Block- eller collapsed-card-drag pending: vänta tills > THRESHOLD.
      // longPress.observeMove har 8 px tolerance och cancellerar timern
      // själv vid större jitter — vi ska INTE eagerly cancel:a här.
      if (blockDragPending && e.touches.length === 1) {
        const touch = e.touches[0];
        const distFromStart = Math.hypot(
          touch.clientX - blockDragPending.startX,
          touch.clientY - blockDragPending.startY,
        );
        longPress.observeMove({ x: touch.clientX, y: touch.clientY });
        if (distFromStart <= TAP_MOVEMENT_THRESHOLD) return;
        const pending = blockDragPending;
        blockDragPending = null;
        longPress.cancel();
        e.preventDefault();
        if (pending.kind === 'collapsed') {
          cbRef.current.onCollapsedGroupDragCommit?.(pending.id, pending.startX, pending.startY);
        } else {
          cbRef.current.onBlockDragCommit?.(pending.id, pending.startX, pending.startY);
        }
        return;
      }

      // Pågående block-/group-/collapsed-drag.
      if (isDraggingRef.current && dragBlockIdRef.current && e.touches.length === 1) {
        e.preventDefault();
        longPress.cancel();
        const touch = e.touches[0];
        const canvasInner = canvas.querySelector('.canvas-inner') as HTMLElement | null;
        if (!canvasInner) return;
        const innerRect = canvasInner.getBoundingClientRect();
        const scaleVal = contentScaleRef.current;
        const originX = canvasOriginXRef.current;
        const originY = canvasOriginYRef.current;
        const currentX = (touch.clientX - innerRect.left) / scaleVal;
        const currentY = (touch.clientY - innerRect.top) / scaleVal;

        const bid = dragBlockIdRef.current;
        if (bid.startsWith('collapsed:')) {
          const groupId = bid.replace('collapsed:', '');
          const x = currentX - dragOffsetRef.current.x - originX;
          const y = currentY - dragOffsetRef.current.y - originY;
          scheduleDragUpdate({ kind: 'collapsed', groupId, x, y });
        } else if (bid.startsWith('group:')) {
          const groupId = bid.replace('group:', '');
          scheduleDragUpdate({ kind: 'group', groupId, currentX, currentY });
        } else {
          const x = currentX - dragOffsetRef.current.x - originX;
          const y = currentY - dragOffsetRef.current.y - originY;
          scheduleDragUpdate({ kind: 'block', blockId: bid, x, y });
        }
        return;
      }

      // 2-finger pan + pinch.
      if (e.touches.length === 2 && currentPan?.fingers === 2) {
        e.preventDefault();
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const pan = currentPan;
        const priorDist = lastTouchDist;
        lastTouchDist = dist;
        // Capture deltat INNAN vi skriver över lastX/Y.
        const panDx = pan.lastX - midX;
        const panDy = pan.lastY - midY;
        if (twoFingerStart) {
          const moveDist = Math.hypot(midX - twoFingerStart.midX, midY - twoFingerStart.midY);
          const distDelta = Math.abs(dist - twoFingerStart.dist);
          if (moveDist > TAP_MOVEMENT_THRESHOLD || distDelta > TAP_MOVEMENT_THRESHOLD) {
            twoFingerMoved = true;
          }
        }
        pan.lastX = midX;
        pan.lastY = midY;
        if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
        pendingRaf = requestAnimationFrame(() => {
          pendingRaf = null;
          if (panDx !== 0) canvas.scrollLeft += panDx;
          if (panDy !== 0) canvas.scrollTop += panDy;
          if (priorDist > 0) {
            const factor = dist / priorDist;
            cbRef.current.setContentScale(prev => clamp(prev * factor));
          }
        });
        return;
      }

      // 1-finger pan på tom canvas. Tap är ändå klassificerad om rörelse
      // var minimal (< TAP_MOVEMENT_THRESHOLD vid touchend).
      if (e.touches.length === 1 && currentPan?.fingers === 1 && oneFingerStart && oneFingerLast) {
        const touch = e.touches[0];
        const panDx = oneFingerLast.x - touch.clientX;
        const panDy = oneFingerLast.y - touch.clientY;
        oneFingerLast = { x: touch.clientX, y: touch.clientY };
        const distFromStart = Math.hypot(
          touch.clientX - oneFingerStart.x,
          touch.clientY - oneFingerStart.y,
        );
        if (distFromStart > TAP_MOVEMENT_THRESHOLD) {
          oneFingerMoved = true;
          longPress.cancel();
        } else {
          longPress.observeMove({ x: touch.clientX, y: touch.clientY });
        }
        e.preventDefault();
        // Live-pan: applicera deltat på scroll-position. Använd
        // requestAnimationFrame för att coalesce:a multiple moves per frame.
        if (oneFingerMoved && (panDx !== 0 || panDy !== 0)) {
          if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
          pendingRaf = requestAnimationFrame(() => {
            pendingRaf = null;
            canvas.scrollLeft += panDx;
            canvas.scrollTop += panDy;
          });
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (gestureStart) {
        const duration = Math.round(performance.now() - gestureStart.t);
        if (gestureStart.kind === 'pinch') {
          const startScale = gestureStart.scale.toFixed(2);
          const endScale = contentScaleRef.current.toFixed(2);
          logger.debug(`gesture: pinch ${startScale}→${endScale} in ${duration}ms`);
        } else {
          logger.debug(`gesture: ${gestureStart.kind} in ${duration}ms`);
        }
        gestureStart = null;
      }
      lastTouchDist = 0;

      // 2-finger tap (båda fingrar ner+upp utan rörelse) → undo
      if (twoFingerStart && !twoFingerMoved && e.touches.length === 0) {
        const elapsed = performance.now() - twoFingerStart.t;
        if (elapsed < 200) {
          logger.debug(`gesture: 2-finger tap → undo (${elapsed}ms)`);
          cbRef.current.onUndo?.();
        }
      }
      twoFingerStart = null;
      twoFingerMoved = false;
      groupDragPending = null;
      blockDragPending = null;

      // Drag-end.
      if (isDraggingRef.current) {
        flushPendingDragUpdate();
        isDraggingRef.current = false;
        dragBlockIdRef.current = null;
        cbRef.current.setIsDragging(false);
        cbRef.current.setDragBlockId(null);
        longPress.cancel();
        oneFingerStart = null;
        oneFingerLast = null;
        oneFingerMoved = false;
        resetPath();
        currentPan = null;
        return;
      }

      // 1-finger end på tom canvas. Om ingen rörelse → tap. Annars
      // gesten var redan en pan (livet hände i touchmove); inget mer
      // att göra vid touchend.
      if (oneFingerStart && currentPan?.fingers === 1) {
        longPress.cancel();
        if (!oneFingerMoved) {
          tapClassifier.registerTap(oneFingerStart);
        }
        oneFingerStart = null;
        oneFingerLast = null;
        oneFingerMoved = false;
        resetPath();
      }

      currentPan = null;
    };

    const handleTouchCancel = () => {
      longPress.cancel();
      tapClassifier.cancel();
      currentPan = null;
      lastTouchDist = 0;
      oneFingerStart = null;
      oneFingerLast = null;
      oneFingerMoved = false;
      twoFingerStart = null;
      twoFingerMoved = false;
      groupDragPending = null;
      blockDragPending = null;
      resetPath();
      cancelPendingDragUpdate();
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        dragBlockIdRef.current = null;
        cbRef.current.setIsDragging(false);
        cbRef.current.setDragBlockId(null);
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      cancelPendingDragUpdate();
      longPress.cancel();
      tapClassifier.cancel();
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchCancel);
    };
    // useEffect re-binds bara när canvasRef ändras (i praktiken aldrig
    // under komponentens livstid). canvasOriginX/YRef, isDraggingRef,
    // dragBlockIdRef, dragOffsetRef är stable RefObjects.
    //
    // ⚠ `pathRefs` är AVSIKTLIGT NOT i deps. Parent skickar in en ny
    // object literal { pathRef, tickRef } varje render — om vi hade den
    // i deps skulle useEffect re-binda DOM-listeners varje render, vilket
    // resettar local state (currentPan, lastTouchDist, etc.) till null
    // mellan touchmove-frames → 2-finger pan/pinch och 1-finger pan
    // kraschar tyst eftersom currentPan blir null direkt efter touchstart.
    // De INRE ref:erna (pathRefs.pathRef, pathRefs.tickRef) är stabila
    // via useRef i parent, så listener:n läser dem korrekt via closure
    // över initial-render-värdet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef, canvasOriginXRef, canvasOriginYRef, isDraggingRef, dragBlockIdRef, dragOffsetRef]);
}
